// Pop-up ao ligar o Pomodoro (AC-068): tomate animado, tempo de foco (25 min · 50 min ·
// a cada tarefa · digitar de 1 min a 12 h) e tempo de descanso (5 · 10 · 15 · 30 min ou
// digitar de 1 segundo a 24 horas — ASM-033 / ASM-035).
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BREAK_SHORTCUTS, FOCUS_SHORTCUTS, POMODORO_LIMITS, type BreakMode } from '../domain/pomodoro';
import { formatDuration, formatMinutes } from '../i18n/format';
import { useAppState } from '../state/AppStore';
import { usePomodoro } from '../state/usePomodoro';
import { Dialog } from './EditTaskDialog';
import { MinutesInput } from './MinutesInput';
import { cls } from './NewTaskForm';
import { Tomato } from './Tomato';

export type DurationUnit = 's' | 'min' | 'h';
const FACTOR: Record<DurationUnit, number> = { s: 1, min: 60, h: 3600 };

/** Mostra a duração na maior unidade exata (90 s → 90 s; 1200 s → 20 min; 7200 s → 2 h). */
export function splitDuration(sec: number): { amount: number; unit: DurationUnit } {
  if (sec % 3600 === 0) return { amount: sec / 3600, unit: 'h' };
  if (sec % 60 === 0) return { amount: sec / 60, unit: 'min' };
  return { amount: sec, unit: 's' };
}

/** null = campo vazio · NaN = valor inválido · número = segundos */
export function toSeconds(amount: string, unit: DurationUnit): number | null {
  if (!amount.trim()) return null;
  const n = Number(amount.replace(',', '.'));
  return Number.isFinite(n) ? Math.round(n * FACTOR[unit]) : Number.NaN;
}

export function isValidBreak(sec: number | null): sec is number {
  const { min, max } = POMODORO_LIMITS.break;
  return sec !== null && Number.isInteger(sec) && sec >= min && sec <= max;
}

/** Campo de duração com unidade (segundos, minutos ou horas). */
export function DurationInput({
  label,
  initialSec,
  onChange,
  onBlur,
  disabled,
}: {
  label: string;
  initialSec: number | null;
  onChange(sec: number | null): void;
  onBlur?(): void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const init = initialSec != null ? splitDuration(initialSec) : null;
  const [amount, setAmount] = useState(init ? String(init.amount) : '');
  const [unit, setUnit] = useState<DurationUnit>(init?.unit ?? 'min');
  return (
    <span className="duration" onBlur={onBlur}>
      <input
        className="input input--num"
        type="number"
        inputMode="decimal"
        min={0}
        step="any"
        placeholder={t('pomodoro.customPlaceholder')}
        aria-label={label}
        value={amount}
        disabled={disabled}
        onChange={(e) => {
          setAmount(e.target.value);
          onChange(toSeconds(e.target.value, unit));
        }}
      />
      <select
        className="input input--unit"
        aria-label={t('pomodoro.unitLabel')}
        value={unit}
        disabled={disabled}
        onChange={(e) => {
          const u = e.target.value as DurationUnit;
          setUnit(u);
          onChange(toSeconds(amount, u));
        }}
      >
        <option value="s">{t('pomodoro.unitSeconds')}</option>
        <option value="min">{t('pomodoro.unitMinutes')}</option>
        <option value="h">{t('pomodoro.unitHours')}</option>
      </select>
    </span>
  );
}

type FocusChoice = number | 'task';

export function PomodoroSetupDialog({ onClose }: { onClose(): void }) {
  const { t } = useTranslation();
  const { controller } = usePomodoro();
  const profile = useAppState((s) => s.profile);
  const lastBreak = profile?.pomodoro_break_sec ?? 300;
  const isShortcut = (BREAK_SHORTCUTS as readonly number[]).includes(lastBreak);
  const [chosen, setChosen] = useState<number>(isShortcut ? lastBreak : 300);
  const [custom, setCustom] = useState<number | null>(isShortcut ? null : lastBreak);
  const [customKey, setCustomKey] = useState(0);
  const [error, setError] = useState(false);

  // foco: 25 · 50 · a cada tarefa · digitado (já preenchido com a última escolha — ASM-034)
  const lastFocus = profile?.pomodoro_focus_min ?? 25;
  const lastIsShortcut = (FOCUS_SHORTCUTS as readonly number[]).includes(lastFocus);
  const [focus, setFocus] = useState<FocusChoice>(
    profile?.pomodoro_break_mode === 'task' ? 'task' : lastIsShortcut ? lastFocus : 25
  );
  const [focusCustom, setFocusCustom] = useState<number | null>(
    profile?.pomodoro_break_mode !== 'task' && !lastIsShortcut ? lastFocus : null
  );
  const [focusKey, setFocusKey] = useState(0);
  const [focusError, setFocusError] = useState(false);
  const pickFocus = (f: FocusChoice) => {
    setFocus(f);
    setFocusCustom(null);
    setFocusKey((k) => k + 1); // limpa o campo "Outro tempo de foco"
    setFocusError(false);
  };

  const confirm = async () => {
    const value = custom !== null ? custom : chosen;
    const focusMin = focusCustom !== null ? focusCustom : focus === 'task' ? null : focus;
    const { min, max } = POMODORO_LIMITS.focus;
    const badFocus = focusMin !== null && (!Number.isInteger(focusMin) || focusMin < min || focusMin > max);
    setFocusError(badFocus);
    setError(!isValidBreak(value));
    if (badFocus || !isValidBreak(value)) return;
    const mode: BreakMode = focusMin === null ? 'task' : 'block';
    if (await controller.setEnabled(true, { breakSec: value, mode, focusMin: focusMin ?? undefined })) onClose();
  };

  return (
    <Dialog labelId="pomodoro-title" onClose={onClose}>
      <div className="pomo-setup" data-testid="pomodoro-setup">
        <Tomato />
        <h2 id="pomodoro-title" className="dialog__title">
          {t('pomodoro.setupTitle')}
        </h2>
        <p className="dialog__body">{t('pomodoro.setupSub')}</p>

        <p className="label-caps">{t('pomodoro.focusLabel')}</p>
        <div className="chips chips--center">
          {FOCUS_SHORTCUTS.map((m) => (
            <button
              key={m}
              type="button"
              className={cls('chip', focusCustom === null && focus === m && 'chip--on')}
              aria-pressed={focusCustom === null && focus === m}
              onClick={() => pickFocus(m)}
            >
              {formatMinutes(m, t)}
            </button>
          ))}
          <button
            type="button"
            className={cls('chip', focusCustom === null && focus === 'task' && 'chip--on')}
            aria-pressed={focusCustom === null && focus === 'task'}
            onClick={() => pickFocus('task')}
          >
            {t('pomodoro.everyTask')}
          </button>
        </div>
        <label className="pomo-setup__custom">
          <span>{t('pomodoro.customLabel')}</span>
          <MinutesInput
            key={focusKey}
            label={t('pomodoro.focusCustomLabel')}
            placeholder={t('pomodoro.focusPlaceholder')}
            initialMinutes={focusKey === 0 ? focusCustom : null}
            onChange={(m) => {
              setFocusCustom(m);
              setFocusError(false);
            }}
          />
        </label>
        {focusError && <p className="field-error">{t('pomodoro.focusRange')}</p>}

        <p className="label-caps">{t('pomodoro.howLongLabel')}</p>
        <div className="chips chips--center">
          {BREAK_SHORTCUTS.map((sec) => (
            <button
              key={sec}
              type="button"
              className={cls('chip', custom === null && chosen === sec && 'chip--on')}
              aria-pressed={custom === null && chosen === sec}
              onClick={() => {
                setChosen(sec);
                setCustom(null);
                setCustomKey((k) => k + 1); // limpa o campo "Outro tempo"
                setError(false);
              }}
            >
              {formatDuration(sec, t)}
            </button>
          ))}
        </div>
        <label className="pomo-setup__custom">
          <span>{t('pomodoro.customLabel')}</span>
          <DurationInput
            key={customKey}
            label={t('pomodoro.customLabel')}
            initialSec={customKey === 0 ? custom : null}
            onChange={(sec) => {
              setCustom(sec);
              setError(false);
            }}
          />
        </label>
        {error && <p className="field-error">{t('pomodoro.durationRange')}</p>}

        <div className="dialog__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button type="button" className="btn btn--primary" onClick={() => void confirm()}>
            <Tomato size={18} />
            {t('pomodoro.turnOn')}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
